import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  List, CalendarDays, ChevronLeft, ChevronRight, MapPin, Users, 
  Download, CalendarPlus, Plus, Check, Trash2, Edit2, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEAM_MEMBERS = ['Renee', 'Biz', 'Ozzy'];

const statusColors = {
  not_applied: 'bg-zinc-700',
  applied: 'bg-yellow-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
  sold_out: 'bg-slate-500',
};

export default function CalendarTasks() {
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('all');
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [viewTaskDialogOpen, setViewTaskDialogOpen] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: 'Renee',
    deadline: new Date().toISOString().split('T')[0],
    priority: 'medium'
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [eventsRes, tasksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/events`, { headers }),
        axios.get(`${BACKEND_URL}/api/tasks`, { headers })
      ]);
      
      setEvents(eventsRes.data.sort((a, b) => new Date(a.start_date) - new Date(b.start_date)));
      setTasks(tasksRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingTask) {
        await axios.put(`${BACKEND_URL}/api/tasks/${editingTask.id}`, taskForm, { headers });
        toast.success('Task updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/tasks`, taskForm, { headers });
        toast.success('Task created');
      }
      
      setTaskDialogOpen(false);
      setEditingTask(null);
      resetTaskForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const toggleTaskComplete = async (task) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const isCompleted = task.status === 'completed';
      const newStatus = isCompleted ? 'pending' : 'completed';
      await axios.put(`${BACKEND_URL}/api/tasks/${task.id}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
      // Update viewingTask if it's the same task
      if (viewingTask && viewingTask.id === task.id) {
        setViewingTask({...viewingTask, status: newStatus});
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
      console.error(error);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      assigned_to: task.assigned_to,
      deadline: task.deadline,
      priority: task.priority || 'medium'
    });
    setTaskDialogOpen(true);
  };

  const resetTaskForm = () => {
    setTaskForm({
      title: '',
      description: '',
      assigned_to: 'Renee',
      deadline: new Date().toISOString().split('T')[0],
      priority: 'medium'
    });
  };

  // Calendar helper functions
  const generateICS = (item, isTask = false) => {
    const formatICSDate = (dateStr) => {
      return dateStr.replace(/-/g, '');
    };
    
    if (isTask) {
      return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Vyrus Hub//Tasks//EN
BEGIN:VTODO
UID:task-${item.id}@vyrushub
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DUE;VALUE=DATE:${formatICSDate(item.deadline)}
SUMMARY:[TASK] ${item.title}
DESCRIPTION:Assigned to: ${item.assigned_to}${item.description ? '\\n' + item.description : ''}
PRIORITY:${item.priority === 'high' ? '1' : item.priority === 'low' ? '9' : '5'}
END:VTODO
END:VCALENDAR`;
    }
    
    return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Vyrus Hub//Events//EN
BEGIN:VEVENT
UID:${item.id}@vyrushub
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DTSTART;VALUE=DATE:${formatICSDate(item.start_date)}
DTEND;VALUE=DATE:${formatICSDate(item.end_date)}
SUMMARY:${item.name}
LOCATION:${item.location}
DESCRIPTION:Status: ${item.application_status.replace('_', ' ')}
END:VEVENT
END:VCALENDAR`;
  };

  const generateMonthICS = (monthYear, items) => {
    const formatICSDate = (dateStr) => dateStr.replace(/-/g, '');
    
    let ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Vyrus Hub//Calendar//EN
`;
    
    items.forEach(item => {
      if (item.type === 'task') {
        ics += `BEGIN:VTODO
UID:task-${item.id}@vyrushub
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DUE;VALUE=DATE:${formatICSDate(item.deadline)}
SUMMARY:[TASK] ${item.title}
DESCRIPTION:Assigned to: ${item.assigned_to}
END:VTODO
`;
      } else {
        ics += `BEGIN:VEVENT
UID:${item.id}@vyrushub
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DTSTART;VALUE=DATE:${formatICSDate(item.start_date)}
DTEND;VALUE=DATE:${formatICSDate(item.end_date)}
SUMMARY:${item.name}
LOCATION:${item.location}
END:VEVENT
`;
      }
    });
    
    ics += 'END:VCALENDAR';
    return ics;
  };

  const downloadICS = (content, filename) => {
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Calendar file downloaded!');
  };

  const getGoogleCalendarUrl = (item, isTask = false) => {
    const date = isTask ? item.deadline.replace(/-/g, '') : item.start_date.replace(/-/g, '');
    const endDate = isTask ? item.deadline.replace(/-/g, '') : item.end_date.replace(/-/g, '');
    const title = encodeURIComponent(isTask ? `[TASK] ${item.title}` : item.name);
    const details = encodeURIComponent(isTask ? `Assigned to: ${item.assigned_to}` : `Status: ${item.application_status}`);
    const location = encodeURIComponent(isTask ? '' : item.location);
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${date}/${endDate}&details=${details}&location=${location}`;
  };

  // Export full month to Google Calendar - opens multiple tabs for each item
  const exportMonthToGoogleCalendar = (monthYear, items) => {
    if (items.length === 0) {
      toast.error('No items to export for this month');
      return;
    }
    
    // Warn user about multiple tabs
    if (items.length > 5) {
      if (!window.confirm(`This will open ${items.length} Google Calendar tabs. Continue?`)) {
        return;
      }
    }
    
    items.forEach((item, index) => {
      // Stagger the tab openings to avoid browser blocking
      setTimeout(() => {
        const url = item.type === 'task' ? getGoogleCalendarUrl(item, true) : getGoogleCalendarUrl(item, false);
        window.open(url, '_blank');
      }, index * 300);
    });
    
    toast.success(`Opening ${items.length} calendar items...`);
  };

  // Get items for current month in calendar view
  const getCurrentMonthItems = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return getAllItems().filter(item => {
      const itemDate = new Date(item.sortDate);
      return itemDate.getFullYear() === year && itemDate.getMonth() === month;
    });
  };

  // Combine events and tasks for display
  const getAllItems = () => {
    const eventItems = events.map(e => ({ ...e, type: 'event', sortDate: e.start_date }));
    const taskItems = tasks.map(t => ({ ...t, type: 'task', sortDate: t.deadline }));
    return [...eventItems, ...taskItems].sort((a, b) => new Date(a.sortDate) - new Date(b.sortDate));
  };

  const getFilteredItems = () => {
    const all = getAllItems();
    if (activeTab === 'all') return all.filter(i => i.type === 'event' || i.status !== 'completed');
    if (activeTab === 'events') return all.filter(i => i.type === 'event');
    if (activeTab === 'completed') return all.filter(i => i.type === 'task' && i.status === 'completed');
    // Case-insensitive comparison for task assignment
    return all.filter(i => i.type === 'task' && i.status !== 'completed' && i.assigned_to?.toLowerCase() === activeTab.toLowerCase());
  };

  const openViewTask = (task) => {
    setViewingTask(task);
    setViewTaskDialogOpen(true);
  };

  const groupByMonth = (items) => {
    const grouped = {};
    items.forEach(item => {
      const date = new Date(item.sortDate);
      const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!grouped[monthYear]) grouped[monthYear] = [];
      grouped[monthYear].push(item);
    });
    return grouped;
  };

  const formatDate = (dateString) => {
    // Parse date string directly without timezone conversion
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateRange = (start, end) => `${formatDate(start)} - ${formatDate(end)}`;

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { daysInMonth: lastDay.getDate(), startingDay: firstDay.getDay() };
  };

  const getItemsForDay = (day) => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const checkStr = checkDate.toISOString().split('T')[0];
    
    return getAllItems().filter(item => {
      if (item.type === 'task') {
        return item.deadline === checkStr;
      }
      const start = new Date(item.start_date);
      const end = new Date(item.end_date);
      return checkDate >= new Date(start.setHours(0,0,0,0)) && checkDate <= new Date(end.setHours(23,59,59,999));
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  const filteredItems = getFilteredItems();
  const groupedItems = groupByMonth(filteredItems);
  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight">Calendar & Tasks</h1>
          <p className="text-muted-foreground text-sm mt-1">Events and tasks in one place</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { resetTaskForm(); setEditingTask(null); setTaskDialogOpen(true); }} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Task
          </Button>
          <div className="flex bg-secondary/30 p-1 rounded-lg">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')}>
              <List className="h-4 w-4" />
            </Button>
            <Button variant={viewMode === 'month' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('month')}>
              <CalendarDays className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs for filtering */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-6 h-auto">
          <TabsTrigger value="all" className="text-xs sm:text-sm py-2">All</TabsTrigger>
          <TabsTrigger value="events" className="text-xs sm:text-sm py-2">Events</TabsTrigger>
          {TEAM_MEMBERS.map(member => (
            <TabsTrigger key={member} value={member} className="text-xs sm:text-sm py-2">{member}</TabsTrigger>
          ))}
          <TabsTrigger value="completed" className="text-xs text-green-500">✓ Done</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Month controls */}
      {viewMode === 'month' && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <h2 className="text-base sm:text-xl font-semibold">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <CalendarPlus className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Export Month</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => {
                  const items = getCurrentMonthItems();
                  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  const ics = generateMonthICS(monthYear, items);
                  downloadICS(ics, `vyrus_${monthYear.replace(/\s+/g, '_')}.ics`);
                }}>
                  <Download className="h-4 w-4 mr-2" />Download .ics File ({getCurrentMonthItems().length} items)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  const items = getCurrentMonthItems();
                  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  exportMonthToGoogleCalendar(monthYear, items);
                }}>
                  <CalendarDays className="h-4 w-4 mr-2" />Add All to Google Calendar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        Object.keys(groupedItems).length === 0 ? (
          <Card className="p-12 text-center bg-card border-border/50">
            <div className="text-muted-foreground">No items found</div>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedItems).map(([monthYear, items]) => (
              <div key={monthYear}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-semibold font-heading text-primary">{monthYear}</h2>
                  <div className="flex gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <CalendarPlus className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Add Month</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => {
                          const ics = generateMonthICS(monthYear, items);
                          downloadICS(ics, `vyrus_${monthYear.replace(/\s+/g, '_')}.ics`);
                        }}>
                          <Download className="h-4 w-4 mr-2" />Download .ics File
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => exportMonthToGoogleCalendar(monthYear, items)}>
                          <CalendarDays className="h-4 w-4 mr-2" />Add All to Google Calendar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="space-y-2">
                  {items.map(item => (
                    <Card key={`${item.type}-${item.id}`} className={`p-3 bg-card border-l-4 ${item.type === 'task' ? 'border-l-orange-500' : 'border-l-cyan-500'}`}>
                      <div className="flex items-start gap-3">
                        {item.type === 'task' ? (
                          <>
                            <button onClick={() => toggleTaskComplete(item)} className="mt-1 flex-shrink-0">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${item.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-muted-foreground hover:border-green-500'}`}>
                                {item.status === 'completed' && <Check className="h-3 w-3 text-white" />}
                              </div>
                            </button>
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openViewTask(item)}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">TASK</span>
                                <span className="text-xs text-muted-foreground">{item.assigned_to}</span>
                                {item.priority && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${item.priority === 'high' ? 'bg-red-500/20 text-red-400' : item.priority === 'low' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                    {item.priority.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <p className={`font-medium text-sm hover:text-primary ${item.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{item.title}</p>
                              <p className="text-xs text-muted-foreground">Due: {formatDate(item.deadline)}</p>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditTask(item)}><Edit2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteTask(item.id)}><Trash2 className="h-4 w-4" /></Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm"><CalendarPlus className="h-4 w-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem onClick={() => downloadICS(generateICS(item, true), `task_${item.title}.ics`)}>
                                    <Download className="h-4 w-4 mr-2" />Download .ics
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => window.open(getGoogleCalendarUrl(item, true), '_blank')}>
                                    <CalendarDays className="h-4 w-4 mr-2" />Google Calendar
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={`w-1 min-h-[40px] rounded-full ${statusColors[item.application_status]}`} />
                            <div className="flex-1 min-w-0" onClick={() => navigate(`/events/${item.id}`)} role="button">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">EVENT</span>
                              </div>
                              <p className="font-medium text-sm cursor-pointer hover:text-primary">{item.name}</p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />{item.location}
                              </p>
                              <p className="text-xs text-muted-foreground">{formatDateRange(item.start_date, item.end_date)}</p>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm"><CalendarPlus className="h-4 w-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => downloadICS(generateICS(item), `${item.name}.ics`)}>
                                  <Download className="h-4 w-4 mr-2" />Download .ics
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => window.open(getGoogleCalendarUrl(item), '_blank')}>
                                  <CalendarDays className="h-4 w-4 mr-2" />Google Calendar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Month View */}
      {viewMode === 'month' && (
        <Card className="bg-card border-border/50 p-2 sm:p-4 overflow-x-auto">
          <div className="grid grid-cols-7 gap-1 mb-2 min-w-[500px]">
            {weekDays.map(day => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 min-w-[500px]">
            {Array.from({ length: startingDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[80px] bg-secondary/10 rounded-lg" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayItems = getItemsForDay(day);
              const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
              return (
                <div key={day} className={`min-h-[80px] p-1 rounded-lg border ${isToday ? 'border-primary bg-primary/5' : 'border-border/30 bg-secondary/10'}`}>
                  <div className={`text-xs font-medium mb-1 ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>{day}</div>
                  <div className="space-y-0.5">
                    {dayItems.slice(0, 3).map(item => (
                      <div
                        key={`${item.type}-${item.id}`}
                        className={`text-[9px] p-0.5 rounded truncate cursor-pointer ${item.type === 'task' ? 'bg-orange-500/20 text-orange-300' : 'bg-cyan-500/20 text-cyan-300'}`}
                        onClick={() => item.type === 'event' && navigate(`/events/${item.id}`)}
                      >
                        {item.type === 'task' ? item.title : item.name}
                      </div>
                    ))}
                    {dayItems.length > 3 && <div className="text-[9px] text-muted-foreground">+{dayItems.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Legend */}
      <Card className="p-3 bg-card border-border/50">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-cyan-500" />
            <span className="text-xs text-muted-foreground">Event</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-orange-500" />
            <span className="text-xs text-muted-foreground">Task</span>
          </div>
        </div>
      </Card>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={(open) => { setTaskDialogOpen(open); if (!open) { setEditingTask(null); resetTaskForm(); } }}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTaskSubmit} className="space-y-4 pt-4">
            <div>
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                required
                className="bg-secondary/50"
              />
            </div>
            <div>
              <Label htmlFor="assigned_to">Assign To</Label>
              <select
                id="assigned_to"
                value={taskForm.assigned_to}
                onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
              >
                {TEAM_MEMBERS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="deadline">Due Date *</Label>
              <Input
                id="deadline"
                type="date"
                value={taskForm.deadline}
                onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })}
                required
                className="bg-secondary/50"
              />
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <select
                id="priority"
                value={taskForm.priority}
                onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                className="bg-secondary/50"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingTask ? 'Update' : 'Add'} Task</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Task Dialog */}
      <Dialog open={viewTaskDialogOpen} onOpenChange={setViewTaskDialogOpen}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Task Details</DialogTitle>
          </DialogHeader>
          {viewingTask && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <button onClick={() => toggleTaskComplete(viewingTask)}>
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${viewingTask.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-muted-foreground hover:border-green-500'}`}>
                    {viewingTask.status === 'completed' && <Check className="h-4 w-4 text-white" />}
                  </div>
                </button>
                <h3 className={`text-lg font-semibold ${viewingTask.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{viewingTask.title}</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Assigned To</p>
                  <p className="font-medium capitalize">{viewingTask.assigned_to}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Due Date</p>
                  <p className="font-medium">{formatDate(viewingTask.deadline)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Priority</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs ${viewingTask.priority === 'high' ? 'bg-red-500/20 text-red-400' : viewingTask.priority === 'low' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {(viewingTask.priority || 'medium').toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs ${viewingTask.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                    {viewingTask.status === 'completed' ? 'COMPLETED' : 'PENDING'}
                  </span>
                </div>
              </div>
              
              {viewingTask.description && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Description</p>
                  <p className="text-sm bg-secondary/30 p-3 rounded-lg">{viewingTask.description}</p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setViewTaskDialogOpen(false)}>Close</Button>
                <Button onClick={() => { setViewTaskDialogOpen(false); openEditTask(viewingTask); }}>
                  <Edit2 className="h-4 w-4 mr-2" />Edit Task
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
